import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { DraftOrder } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useCustomizations, useSizeSets } from '@/features/dictionary';
import { StoreVariantPicker } from '@/features/resource-pickers';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyProductLineForm,
  linesInput,
  toProductLineInput,
  withLineAdded,
} from '../api/mappers';
import { useUpdateDraftOrder } from '../hooks/use-draft-order-mutations';
import { customizationsFor, emptyCustomizationValues } from '../schemas/customization-rules';
import { isStateConflict, lineFieldPath } from '../schemas/draft-order-rules';
import {
  productLineSchema,
  type ProductLineFormInput,
  type ProductLineFormOutput,
} from '../schemas/line-schemas';

import { CustomizationField } from './customization-field';

export interface AddProductLineDialogProps {
  storeId: string;
  draft: DraftOrder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The variant chosen in the picker, with what the line form needs of its product. */
interface PickedVariant {
  storeProductId: string;
  productTitle: string;
  variantTitle: string;
  sku: string | null;
  /** The store's effective price, formatted; null when unpriced. */
  price: string | null;
  customizationIds: string[];
}

const PUBLISHED = ['published'] as const;

/**
 * Opens on the variant picker over what is published in this store (priced per store); the
 * chosen variant then gets a quantity, the product's customizations and an optional price
 * override. Saving appends the line to the full list; a 422 about the new line lands on its
 * field. Cancelling the first pick closes the whole flow.
 */
export function AddProductLineDialog({
  storeId,
  draft,
  open,
  onOpenChange,
}: AddProductLineDialogProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const message = useErrorMessage();
  const format = useFormat();
  const update = useUpdateDraftOrder(storeId, 'draftOrders:lines.saved');
  const [picking, setPicking] = useState(true);
  const [picked, setPicked] = useState<PickedVariant | null>(null);
  // Read when the picker closes, in the same tick as the confirm that set `picked`.
  const hasPick = useRef(false);

  const dictionary = useCustomizations();
  const sizeSets = useSizeSets();
  const defs = useMemo(
    () =>
      customizationsFor(picked?.customizationIds ?? [], dictionary.data ?? [], sizeSets.data ?? []),
    [picked, dictionary.data, sizeSets.data],
  );
  const schema = useMemo(() => productLineSchema(defs), [defs]);
  const form = useForm<ProductLineFormInput, unknown, ProductLineFormOutput>({
    resolver: useI18nResolver(schema),
    defaultValues: emptyProductLineForm(),
  });
  const overridePrice = form.watch('overridePrice');

  const close = () => {
    onOpenChange(false);
    update.reset();
  };

  const onPickerOpenChange = (next: boolean) => {
    setPicking(next);
    if (!next && !hasPick.current) close();
  };

  const submit = form.handleSubmit(async (values) => {
    const added = toProductLineInput(values, defs);
    const index = linesInput(draft.lines).length;
    const keys = added.kind === 'variant' ? (added.customizations ?? []).map((c) => c.key) : [];
    try {
      await update.mutateAsync({
        id: draft.id,
        body: { lines: withLineAdded(draft.lines, added) },
      });
      close();
    } catch (error) {
      const applied = applyFieldErrors(error, form.setError, {
        knownFields: new Set([
          'variantId',
          'quantity',
          'unitPrice',
          ...defs.map((d) => `customizations.${d.key}`),
        ]),
        mapField: (field) => lineFieldPath(field, index, keys) ?? 'root.server',
      });
      if (applied) return;
      form.setError('root.server', {
        type: 'server',
        message: isStateConflict(error) ? t('draftOrders:errors.locked') : message(error),
      });
    }
  });

  return (
    <>
      <StoreVariantPicker
        open={open && picking}
        onOpenChange={onPickerOpenChange}
        storeId={storeId}
        statuses={PUBLISHED}
        {...(picked ? { initialSelectedVariantIds: [form.getValues('variantId')] } : {})}
        onConfirm={(selection) => {
          const [choice] = selection.children;
          if (!choice) return;
          const { item: row, child: variant } = choice;
          const next: PickedVariant = {
            storeProductId: row.id,
            productTitle: row.title ?? row.product.title,
            variantTitle: variant.title,
            sku: variant.skuOverride ?? variant.sku,
            price: variant.effective ? format.money(variant.effective) : null,
            customizationIds: row.product.customizationIds,
          };
          hasPick.current = true;
          setPicked(next);
          const nextDefs = customizationsFor(
            next.customizationIds,
            dictionary.data ?? [],
            sizeSets.data ?? [],
          );
          form.reset({
            ...emptyProductLineForm(),
            storeProductId: next.storeProductId,
            variantId: variant.variantId,
            quantity: form.getValues('quantity'),
            customizations: emptyCustomizationValues(nextDefs),
          });
          update.reset();
        }}
      />
      <Dialog
        open={open && picked !== null}
        onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      >
        <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('draftOrders:picker.title')}</DialogTitle>
            <DialogDescription>{t('draftOrders:picker.description')}</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={submit} noValidate className="flex flex-col gap-4">
              {form.formState.errors.root?.server?.message ? (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
                </Alert>
              ) : null}
              {picked ? (
                <>
                  <FormField
                    control={form.control}
                    name="variantId"
                    render={() => (
                      <FormItem>
                        <FormLabel>{t('draftOrders:picker.variant')}</FormLabel>
                        <div className="flex items-center gap-3 rounded-md border p-3">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{picked.productTitle}</p>
                            <p className="text-muted-foreground truncate text-xs">
                              {[picked.variantTitle, picked.sku, picked.price]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setPicking(true)}
                          >
                            {t('draftOrders:picker.changeVariant')}
                          </Button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="quantity"
                    render={({ field }) => (
                      <FormItem className="sm:max-w-28">
                        <FormLabel>{t('draftOrders:picker.quantity')}</FormLabel>
                        <FormControl>
                          <Input type="number" inputMode="numeric" min={1} max={99} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {defs.length > 0 ? (
                    <fieldset className="flex flex-col gap-3">
                      <legend className="mb-2 text-sm font-medium">
                        {t('draftOrders:picker.customizations')}
                      </legend>
                      {defs.map((def) => (
                        <FormField
                          key={def.id}
                          control={form.control}
                          name={`customizations.${def.key}`}
                          render={({ field, fieldState }) => (
                            <CustomizationField
                              def={def}
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              name={field.name}
                              inputRef={field.ref}
                              invalid={fieldState.invalid}
                            />
                          )}
                        />
                      ))}
                    </fieldset>
                  ) : null}
                  <FormField
                    control={form.control}
                    name="overridePrice"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start justify-between gap-4 rounded-md border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>{t('draftOrders:picker.overridePrice')}</FormLabel>
                          {picked.price ? (
                            <FormDescription>
                              {t('draftOrders:picker.overridePriceHint', { price: picked.price })}
                            </FormDescription>
                          ) : null}
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                              if (!checked) {
                                form.setValue('unitPrice', null);
                                form.clearErrors('unitPrice');
                              }
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  {overridePrice ? (
                    <FormField
                      control={form.control}
                      name="unitPrice"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t('draftOrders:picker.unitPrice')}</FormLabel>
                          <FormControl>
                            <MoneyInput
                              currency={draft.currency}
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              aria-invalid={fieldState.invalid}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}
                </>
              ) : null}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={close}>
                  {t('common:actions.cancel')}
                </Button>
                <Button type="submit" disabled={update.isPending}>
                  {update.isPending
                    ? t('draftOrders:picker.submitting')
                    : t('draftOrders:picker.submit')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
